import { types, flow, getRoot, getParent } from "mobx-state-tree"

import Network from "~/stores/models/Network"
import TicketResult from "./TicketResult"
import { lookup } from "~/services/parking/fetcher"
import buildCustomValidationStore from "~/stores/models/customValidation"
import { PARKING_FLOW_TYPE } from "~/utilities/constants"
import TRACKING from "~/services/tracking"

const MAX_SELECTABLE = 15
const MAX_PAYABLE_AMOUNT = 1000

const ResultsStoreBase = types
  .model("ResultsStore", {
    found: types.array(TicketResult),
    fightSelected: types.array(types.safeReference(TicketResult)),
    paySelected: types.array(types.safeReference(TicketResult)),
    service: types.optional(Network, {}),
    serviceFee: types.maybeNull(types.number, 0),
    additionalFeePerTicket: types.maybeNull(types.number, 0),
    creditCardFee: types.maybeNull(types.number, 0)
  })
  .views(self => ({
    get isSelectAllPayableEnabled() {
      return self.payableSelectableCount <= MAX_SELECTABLE &&
        self.totalPayableSelectableOwed <= MAX_PAYABLE_AMOUNT
    },
    get isSelectAllFightableEnabled() {
      return self.fightableSelectableCount <= MAX_SELECTABLE
    },
    canSelectAdditionalPayableTicket(ticket) {
      return self.paySelected.length < MAX_SELECTABLE &&
        self.totalSelectedPayableAmount <= MAX_PAYABLE_AMOUNT &&
        self.totalSelectedPayableAmount + Number(ticket.amountDue) <= MAX_PAYABLE_AMOUNT;
    },
    canSelectAdditionalFightableTicket() {
      return self.fightSelected.length < MAX_SELECTABLE
    },
    get paySubtotal() {
      const { user } = self
      const userCredits = user.credits ? user.credits : 0
      const sum =
        self.totalServiceFee +
        self.feeForSelectedPayable +
        self.totalSelectedPayableAmount -
        userCredits
      return sum < 0 ? 0 : sum
    },
    get totalServiceFee() {
      return self.serviceFee + self.additionalTicketsFee
    },
    get additionalTicketsFee() {
      if (self.paySelected.length <= 0) return 0
      return (self.paySelected.length - 1) * self.additionalFeePerTicket
    },
    get feeForSelectedPayable() {
      return (self.totalSelectedPayableAmount * self.creditCardFee)
    },
    get allFightableTicketsSelected() {
      const selected = new Set([...self.fightSelected.map(item => item.objectId)])
      return self.fightableSelectable.reduce((allSelected, item) => {
        return allSelected && selected.has(item.objectId);
      }, true) && self.fightableSelectableCount === self.fightSelected.length;
    },
    get allPayableTicketsSelected() {
      const selected = new Set([...self.paySelected.map(item => item.objectId)])
      return self.payableSelectable.reduce((allSelected, item) => {
        return allSelected && selected.has(item.objectId);
      }, true) && self.payableSelectableCount === self.paySelected.length;
    },
    get totalOwed() {
      const sum = self.found.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum.toFixed(2)
    },
    get totalFightableOwed() {
      const sum = self.fightable.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum.toFixed(2)
    },
    get totalFightableSelectableOwed() {
      const sum = self.fightableSelectable.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum.toFixed(2)
    },
    get totalPayableOwed() {
      const sum = self.payable.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum.toFixed(2)
    },
    get totalPayableSelectableOwed() {
      const sum = self.payableSelectable.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum.toFixed(2)
    },
    get totalSelectedPayableAmount() {
      const sum = self.paySelected.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum
    },
    get selectedPayableAmounts() {
      const amountsList = self.paySelected.map(ticket => Number(ticket.amountDue).toFixed(2))
      return amountsList
    },
    get totalSelectedFightableAmount() {
      const sum = self.fightSelected.reduce((amount, ticket) => {
        return amount + Number(ticket.amountDue)
      }, 0)
      return sum.toFixed(2)
    },
    get totalFightableSavings() {
      return (Number(self.totalSelectedFightableAmount) / 2).toFixed(2)
    },
    get totalPayableSavings() {
      return (self.totalSelectedPayableAmount / 2).toFixed(2)
    },
    get fightable() {
      return self.found.filter(ticket => {
        return ticket.isFightableTicket
      })
    },
    get fightableSelectable() {
      return self.fightable.filter(ticket => {
        return !ticket.nonActionable
      })
    },
    get fightableCount() {
      return self.fightable.length
    },

    get diffFightablePlatesCount() {
      return new Set([...self.fightable.map(item => item.plateNumber)]).size
    },

    get fightableSelectableCount() {
      return self.fightableSelectable.length
    },
    get allFightable() {
      return self.found.filter(ticket => {
        return ticket.fightable
      })
    },
    get payable() {
      // SWITCH TO HIDE FIGHTABLE TICKETS FROM PAY FLOW
      if (self.flow.entryType === PARKING_FLOW_TYPE.both) {
        return self.found.filter(ticket => {
          return ticket.isPayableTicket && !ticket.isFightableTicket
        })
      }
      return self.found.filter(ticket => {
        return ticket.isPayableTicket
      })
    },
    get payableSelectable() {
      return self.payable.filter(ticket => {
        return !ticket.nonActionable
      })
    },
    get payableCount() {
      return self.payable.length
    },

    get diffPayablePlatesCount() {
      return new Set([...self.payable.map(item => item.plateNumber)]).size
    },

    get payableSelectableCount() {
      return self.payableSelectable.length
    },
    get allPayable() {
      // SWITCH TO HIDE FIGHTABLE TICKETS FROM PAY FLOW
      if (self.flow.entryType === PARKING_FLOW_TYPE.both) {
        return self.found.filter(ticket => {
          return !ticket.isFightableTicket
        })
      }
      return self.found
    },
    get nonFightableButPayable() {
      return self.payableSelectableCount - self.fightableSelectableCount
    },
    get nonPayableButFightable() {
      return self.fightableSelectableCount - self.payableSelectableCount
    },
    get user() {
      const root = getRoot(self)
      return root.auth.user
    },
    get flow() {
      const parkingFlow = getParent(self)
      return parkingFlow.flow
    },
    isSelectedPayable(ticket) {
      return !!self.paySelected.find(item => {
        return item.ticketID === ticket.ticketID
      })
    },
    isSelectedFightable(ticket) {
      return !!self.fightSelected.find(item => {
        return item.ticketID === ticket.ticketID
      })
    }
  }))
  .actions(self => ({
    clearSelected() {
      self.paySelected.clear()
      self.fightSelected.clear()
    },
    showCantToggleMoreAlert(ticket, flowType) {
      let message = `Sorry we only allow ${MAX_SELECTABLE} tickets to be selected per transaction.`
      if (flowType === PARKING_FLOW_TYPE.pay && self.totalSelectedPayableAmount + Number(ticket.amountDue) >= MAX_PAYABLE_AMOUNT) {
        message = `Sorry there is a limit of $${MAX_PAYABLE_AMOUNT} per transaction. Can not add $${ticket.amountDue} to your current total: $${self.totalSelectedPayableAmount.toFixed(2)}.`
      }
      self.showErrorToast(message)
    },
    toggleFightableTicket(ticket) {
      if (ticket.nonActionable) return false
      self.addTouched("fightSelected")
      if (self.isSelectedFightable(ticket)) {
        self.fightSelected = self.fightSelected.filter(item => {
          return item.ticketID !== ticket.ticketID
        })
      } else {
        if (!self.canSelectAdditionalFightableTicket(ticket)) return self.showCantToggleMoreAlert(ticket, PARKING_FLOW_TYPE.fight)
        self.fightSelected.push(ticket)
      }
    },
    togglePayableTicket(ticket) {
      if (ticket.nonActionable) return false
      self.addTouched("paySelected")
      if (self.isSelectedPayable(ticket)) {
        self.paySelected = self.paySelected.filter(item => {
          return item.ticketID !== ticket.ticketID
        })
      } else {
        if (!self.canSelectAdditionalPayableTicket(ticket)) return self.showCantToggleMoreAlert(ticket, PARKING_FLOW_TYPE.pay)
        self.paySelected.push(ticket)
      }
    },
    toggleAllPayableSelected() {
      if (!self.isSelectAllPayableEnabled) return self.paySelected.clear()
      self.addTouched("paySelected")
      if (self.allPayableTicketsSelected) {
        self.paySelected.clear()
      } else {
        // due to issue directly assigning
        self.paySelected = self.payableSelectable.slice()
      }
    },
    toggleAllFightableSelected() {
      if (!self.isSelectAllFightableEnabled) return self.fightSelected.clear()
      self.addTouched("fightSelected")
      if (self.allFightableTicketsSelected) {
        self.fightSelected.clear()
      } else {
        self.fightSelected = self.fightableSelectable
      }
    },
    async setPlate() {
      if(self.found.length) {
        const ticketInfo = self.found.find(
          (t) => t && (t.plateNumber || t.plateId) && t.state
        )
        const plateNumber = ticketInfo
          ? ticketInfo.plateNumber || ticketInfo.plateId
          : null
        const state = ticketInfo && ticketInfo.state
        if(plateNumber && state) {
          await self.flow.setPlateIfExistent(plateNumber, state)
        }
      }
      else {
        const parkingFlow = getParent(self)
        const { plateNumber, plateState } = parkingFlow.searchCriteria
        await self.flow.setPlateIfExistent(plateNumber, plateState)
      }
    },
    fetch: flow(function* fetchGen(searchCriteria, urlLocation, currentHost) {
      if (self.service.isLoading) return false

      self.service.requestReset()
      self.service.requestStart()

      try {
        const results = yield lookup(searchCriteria, urlLocation, currentHost)
        self.setResults(results)
        self.service.requestComplete()
        yield self.setPlate()

        const root = getRoot(self)
        if (root.ui.isPayNYCFlow) {
          TRACKING.paynycParking.results(root.ui.isPayNYCFlow, results.length)
        }

        if (results.length > 0) {
          TRACKING.parking.fetcherResults(
            self.flow.entryType,
            root.ui.currentHost
          )
        }

        return true
      } catch (error) {
        const additionalMessage = typeof error === "string" ? error : ""
        self.showErrorToast(
          `We could not search for tickets. ${additionalMessage}`
        )
        self.service.requestError(error)
        return false
      }
    }),
    setResults(tickets) {
      self.found = tickets.sort((ticketA,ticketB) => {
        if (ticketA.plateNumber < ticketB.plateNumber) return -1
        if (ticketA.plateNumber > ticketB.plateNumber) return 1
        return 0
      }).map(ticket => TicketResult.create(ticket))
    },
    setPayableSelected(payableTickets) {
      self.paySelected = payableTickets
    },
    setFightableSelected(fightableTickets) {
      self.fightSelected = fightableTickets
    },
    setServiceFee(serviceFee) {
      self.serviceFee = serviceFee
    },
    setAdditionalFeePerTicket(additionalFeePerTicket) {
      self.additionalFeePerTicket = additionalFeePerTicket
    },
    setCreditCardFee(creditCardFee) {
      self.creditCardFee = creditCardFee
    },
    showErrorToast(message) {
      const rootStore = getRoot(self)
      rootStore.ui.toasts.addToast({
        message,
        type: "ERROR"
      })
    }
  }))

const CustomValidationStore = buildCustomValidationStore({
  validationObject: {
    fightSelected: value => {
      if (!value || !value.length) {
        return "Please select a ticket to fight"
      }
      return false
    },
    paySelected: value => {
      if (!value || !value.length) {
        return "Please select a ticket to pay"
      }
      return false
    }
  }
})

export const ResultsStore = types.compose(
  ResultsStoreBase,
  CustomValidationStore
)
